import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Ban, Check, Edit, Eye, Plus, Trash2, User } from 'lucide-react';
import { Status } from '@pillar-portal/database/dist/frontend';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    InfiniteScrollListEmptyState,
    InfiniteScrollListErrorState,
    InfiniteScrollListLoadingState,
} from '@/components/ui/infinite-scroll-list-state';
import { useInfiniteScrollList } from '@/hooks/useInfiniteScrollList';
import { authApi, permissionsApi } from '@/lib/pillarApi';
import { cn, runInBackground } from '@/lib/utils';
import { useAuthStore } from '@/store/auth';
import { useDataInvalidationStore } from '@/store/dataInvalidation';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import { useUnsavedChangesRegistration } from '@/lib/hooks/useUnsavedChangesGuard';
import type {
    ActiveRoleAssignmentResponse,
    PolicyPermissionBundleListItem,
    PermissionsBundlePublic,
} from '@pillar-portal/api-types';
import { hasInternalAdminPrivileges } from '@pillar-portal/api-types';
import { PolicyPermissionBundleModal } from './PolicyPermissionBundleModal';

export const PolicyPermissionBundlesCardOpaqueCursorSchema: z.ZodType<string> = z
    .string()
    .trim()
    .min(1);

function applyLocalBundleStatus(
    bundle: PolicyPermissionBundleListItem,
    statusOverrides: Readonly<Record<string, Status>>
): PolicyPermissionBundleListItem {
    const nextStatus: Status | undefined = statusOverrides[bundle.permissions_bundle_uuid];
    if (nextStatus === undefined || nextStatus === bundle.status) {
        return bundle;
    }
    return {
        ...bundle,
        status: nextStatus,
    };
}

export interface PolicyPermissionBundleFocusRequest {
    readonly permissionsBundleUuid: string;
    readonly requestId: number;
}

interface PolicyPermissionBundlesCardProps {
    readonly organizationUuid: string;
    readonly organizationName: string;
    readonly compactBottomSpacing?: boolean;
    readonly allowSubsidiariesToggle?: boolean;
    readonly defaultCollapsed?: boolean;
    readonly focusPermissionsBundleUuid?: string | null;
    readonly focusPermissionsBundleRequest?: PolicyPermissionBundleFocusRequest | null;
}

export function PolicyPermissionBundlesCard(props: PolicyPermissionBundlesCardProps): ReactElement {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const policyBundlesVersion: number = useDataInvalidationStore(
        (state) => state.policyBundlesVersion
    );
    const [isExpanded, setIsExpanded] = useState<boolean>(props.defaultCollapsed !== true);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    const { data: activeRoleAssignment, isLoading: isActiveRoleAssignmentLoading } =
        useQuery<ActiveRoleAssignmentResponse | null>({
            queryKey: ['activeRoleAssignment'],
            queryFn: async () => {
                return await authApi.getActiveRoleAssignment();
            },
            staleTime: 30000,
            enabled: isExpanded,
        });

    const canCreateBundles: boolean = hasInternalAdminPrivileges(activeRoleAssignment ?? null);
    const [selectedBundle, setSelectedBundle] = useState<PermissionsBundlePublic | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
    const [showDeleted, setShowDeleted] = useState<boolean>(false);
    const [pendingDeleteBundle, setPendingDeleteBundle] = useState<PermissionsBundlePublic | null>(
        null
    );
    const [includeDescendants, setIncludeDescendants] = useState<boolean>(false);
    const [statusOverrides, setStatusOverrides] = useState<Record<string, Status>>({});
    const [focusedBundleUuid, setFocusedBundleUuid] = useState<string | null>(null);
    const [lastFocusedBundleRequestKey, setLastFocusedBundleRequestKey] = useState<string | null>(
        null
    );
    const focusedBundleRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const hasUnsavedChanges: boolean = isCreateModalOpen || isEditModalOpen;
    useUnsavedChangesRegistration(hasUnsavedChanges);

    const deleteMutation = useMutation({
        mutationFn: async (bundleUuid: string) => {
            await permissionsApi.deletePolicyPermissionBundle(bundleUuid);
        },
        onSuccess: () => {
            useDataInvalidationStore.getState().bumpPolicyBundlesVersion();
            setPendingDeleteBundle(null);
        },
    });
    const setActiveMutation = useMutation({
        mutationFn: async (params: { permissionsBundleUuid: string; isActive: boolean }) => {
            await permissionsApi.updatePolicyPermissionBundle(params.permissionsBundleUuid, {
                status: params.isActive ? Status.ACTIVE : Status.DISABLED,
            });
        },
        onSuccess: (_response, variables) => {
            setStatusOverrides((currentValue) => ({
                ...currentValue,
                [variables.permissionsBundleUuid]: variables.isActive
                    ? Status.ACTIVE
                    : Status.DISABLED,
            }));
        },
    });

    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => [
            'policyBundles',
            `org=${props.organizationUuid}`,
            `role=${activeRoleAssignmentId ?? 'none'}`,
            `v=${policyBundlesVersion}`,
            `descendants=${includeDescendants}`,
            `showDeleted=${showDeleted}`,
        ],
        [
            activeRoleAssignmentId,
            includeDescendants,
            policyBundlesVersion,
            props.organizationUuid,
            showDeleted,
        ]
    );

    const list = useInfiniteScrollList<
        PolicyPermissionBundleListItem,
        string,
        ReadonlyArray<string>
    >({
        queryKeyPrefix,
        pageSize: 50,
        fetchPage: async (params) => {
            const response = await permissionsApi.searchPolicyPermissionBundlesForOrganization(
                props.organizationUuid,
                {
                    ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                    limit: params.limit,
                    cursor: params.cursor,
                    include_descendants: includeDescendants,
                    show_deleted: showDeleted ? true : undefined,
                }
            );
            return {
                items: showDeleted
                    ? response.bundles.filter((bundle) => bundle.status === Status.DELETED)
                    : response.bundles.filter((bundle) => bundle.status !== Status.DELETED),
                pagination: response.pagination,
            };
        },
        parseCursorParam: (pageParam: unknown) => {
            if (pageParam === null || pageParam === undefined) {
                return undefined;
            }
            const parseResult = PolicyPermissionBundlesCardOpaqueCursorSchema.safeParse(pageParam);
            if (!parseResult.success) {
                throw new Error('Expected opaque cursor');
            }
            return parseResult.data;
        },
        enabled: isExpanded && !isActiveRoleAssignmentLoading,
        observerRootRef: scrollContainerRef,
    });
    const visibleBundles: ReadonlyArray<PolicyPermissionBundleListItem> = useMemo(
        () => list.items.map((bundle) => applyLocalBundleStatus(bundle, statusOverrides)),
        [list.items, statusOverrides]
    );
    const focusRequest: PolicyPermissionBundleFocusRequest | null = useMemo(() => {
        if (props.focusPermissionsBundleRequest !== undefined) {
            return props.focusPermissionsBundleRequest;
        }
        if (
            props.focusPermissionsBundleUuid === null ||
            props.focusPermissionsBundleUuid === undefined
        ) {
            return null;
        }
        return {
            permissionsBundleUuid: props.focusPermissionsBundleUuid,
            requestId: 0,
        };
    }, [props.focusPermissionsBundleRequest, props.focusPermissionsBundleUuid]);

    useEffect(() => {
        if (focusRequest !== null) {
            setIsExpanded(true);
        }
    }, [focusRequest]);

    useEffect(() => {
        if (focusRequest === null || !isExpanded) {
            return;
        }
        const focusPermissionsBundleUuid: string = focusRequest.permissionsBundleUuid;
        const focusRequestKey = `${focusRequest.requestId}:${focusPermissionsBundleUuid}`;
        if (focusRequestKey === lastFocusedBundleRequestKey) {
            return;
        }

        const visibleBundle: PolicyPermissionBundleListItem | undefined = visibleBundles.find(
            (bundle) => bundle.permissions_bundle_uuid === focusPermissionsBundleUuid
        );
        if (visibleBundle !== undefined) {
            setFocusedBundleUuid(focusPermissionsBundleUuid);
            setLastFocusedBundleRequestKey(focusRequestKey);
            requestAnimationFrame(() => {
                const rowElement: HTMLDivElement | undefined =
                    focusedBundleRowRefs.current.get(focusPermissionsBundleUuid);
                rowElement?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                rowElement?.focus({ preventScroll: true });
            });
            return;
        }

        if (
            list.hasNextPage &&
            !list.isLoading &&
            !list.isFetching &&
            !list.isFetchingNextPage
        ) {
            runInBackground(list.fetchNextPage(), () => {});
            return;
        }

        if (list.pagination !== null && !list.isLoading && !list.isFetching) {
            setLastFocusedBundleRequestKey(focusRequestKey);
        }
    }, [
        focusRequest,
        isExpanded,
        lastFocusedBundleRequestKey,
        list.fetchNextPage,
        list.hasNextPage,
        list.isFetching,
        list.isFetchingNextPage,
        list.isLoading,
        list.pagination,
        visibleBundles,
    ]);

    return (
        <div>
            <Card className="mb-6">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Permissions Policies</CardTitle>
                    <div className="flex items-center gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setIsExpanded((currentValue) => !currentValue);
                            }}
                        >
                            {isExpanded ? 'Collapse' : 'Expand'}
                        </Button>
                        {isExpanded && props.allowSubsidiariesToggle === true ? (
                            <label className="flex items-center gap-2 text-sm">
                                <Checkbox
                                    checked={includeDescendants}
                                    onCheckedChange={(checked) => {
                                        setIncludeDescendants(checked === true);
                                    }}
                                />
                                <span>Show subsidiaries</span>
                            </label>
                        ) : null}
                        {isExpanded && canCreateBundles ? (
                            <Button
                                onClick={() => {
                                    setSelectedBundle(null);
                                    setIsCreateModalOpen(true);
                                }}
                                size="sm"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Create Policy Bundle
                            </Button>
                        ) : null}
                    </div>
                </CardHeader>
                {isExpanded ? (
                    <CardContent>
                        <div className="mb-4 flex items-center justify-between gap-4">
                            <Input
                                placeholder="Search..."
                                value={list.queryText}
                                onChange={(event) => {
                                    list.setQueryText(event.target.value);
                                }}
                                className="w-[320px]"
                            />
                            {canCreateBundles ? (
                                <div className="flex items-center gap-2">
                                    <Label
                                        htmlFor="policy-bundles-show-deleted"
                                        className="cursor-pointer"
                                    >
                                        Show deleted
                                    </Label>
                                    <Checkbox
                                        id="policy-bundles-show-deleted"
                                        checked={showDeleted}
                                        onCheckedChange={(checked) => {
                                            setShowDeleted(checked === true);
                                        }}
                                    />
                                </div>
                            ) : null}
                        </div>

                        {deleteMutation.error ? (
                            <Alert variant="destructive" className="mb-4">
                                <AlertDescription>
                                    {deleteMutation.error instanceof Error
                                        ? deleteMutation.error.message
                                        : 'Failed to delete policy bundle'}
                                </AlertDescription>
                            </Alert>
                        ) : null}
                        {setActiveMutation.error ? (
                            <Alert variant="destructive" className="mb-4">
                                <AlertDescription>
                                    {setActiveMutation.error instanceof Error
                                        ? setActiveMutation.error.message
                                        : 'Failed to update policy bundle'}
                                </AlertDescription>
                            </Alert>
                        ) : null}

                        {list.error !== null ? (
                            <InfiniteScrollListErrorState
                                error={list.error}
                                genericErrorLabel="Failed to load policy bundles."
                            />
                        ) : list.isLoading && list.items.length === 0 ? (
                            <InfiniteScrollListLoadingState label="Loading policy bundles..." />
                        ) : visibleBundles.length === 0 ? (
                            <InfiniteScrollListEmptyState label="No policy bundles found." />
                        ) : (
                            <>
                                <div
                                    ref={scrollContainerRef}
                                    className="max-h-[42rem] space-y-3 overflow-y-auto pr-1"
                                >
                                    {visibleBundles.map(
                                        (bundle: PolicyPermissionBundleListItem) => {
                                            const grantedRoleAssignmentLabel =
                                                bundle.granted_role_assignment_count === 1
                                                    ? '1 role assignment'
                                                    : `${bundle.granted_role_assignment_count} role assignments`;
                                            const policyMetadataLabel =
                                                `${grantedRoleAssignmentLabel}, ` +
                                                `${bundle.permissions.length} permissions`;
                                            return (
                                                <div
                                                    key={bundle.permissions_bundle_uuid}
                                                    id={`policy-permission-bundle-${bundle.permissions_bundle_uuid}`}
                                                    ref={(element) => {
                                                        if (element === null) {
                                                            focusedBundleRowRefs.current.delete(
                                                                bundle.permissions_bundle_uuid
                                                            );
                                                            return;
                                                        }
                                                        focusedBundleRowRefs.current.set(
                                                            bundle.permissions_bundle_uuid,
                                                            element
                                                        );
                                                    }}
                                                    tabIndex={-1}
                                                    className={cn(
                                                        'border rounded-lg p-4 hover:bg-gray-50 transition-colors',
                                                        focusedBundleUuid ===
                                                            bundle.permissions_bundle_uuid
                                                            ? 'border-primary shadow-[inset_0_0_0_1px_hsl(var(--primary))]'
                                                            : null
                                                    )}
                                                >
                                            <div className="flex justify-between items-start gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                        <h4 className="font-medium">
                                                            {bundle.name}
                                                        </h4>
                                                        {bundle.status !== Status.ACTIVE ? (
                                                            <Badge variant="destructive">
                                                                {bundle.status === Status.DELETED
                                                                    ? 'Deleted'
                                                                    : 'Disabled'}
                                                            </Badge>
                                                        ) : null}
                                                    </div>
                                                    {bundle.description !== null &&
                                                    bundle.description.length > 0 ? (
                                                        <div className="text-sm text-muted-foreground space-y-1">
                                                            <div>{bundle.description}</div>
                                                            <div>{policyMetadataLabel}</div>
                                                        </div>
                                                    ) : (
                                                        <div className="text-sm text-muted-foreground space-y-1">
                                                            <div>{policyMetadataLabel}</div>
                                                        </div>
                                                    )}
                                                    {bundle.permissions_deserialization_error !==
                                                    undefined ? (
                                                        <p className="text-sm text-destructive mt-2">
                                                            Invalid persisted permissions payload.
                                                            Open to review and repair.
                                                        </p>
                                                    ) : null}
                                                </div>
                                                <div className="flex gap-2 ml-4">
                                                    {bundle.status !== Status.DELETED ? (
                                                        <>
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                title="View policy bundle"
                                                                onClick={() => {
                                                                    setSelectedBundle(bundle);
                                                                    setIsViewModalOpen(true);
                                                                }}
                                                            >
                                                                <Eye className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                title="Edit policy bundle"
                                                                onClick={() => {
                                                                    setSelectedBundle(bundle);
                                                                    setIsEditModalOpen(true);
                                                                }}
                                                            >
                                                                <Edit className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                title={
                                                                    bundle.status === Status.ACTIVE
                                                                        ? 'Disable policy bundle'
                                                                        : 'Enable policy bundle'
                                                                }
                                                                onClick={() => {
                                                                    setActiveMutation.mutate({
                                                                        permissionsBundleUuid:
                                                                            bundle.permissions_bundle_uuid,
                                                                        isActive:
                                                                            bundle.status !==
                                                                            Status.ACTIVE,
                                                                    });
                                                                }}
                                                                disabled={
                                                                    setActiveMutation.isPending
                                                                }
                                                            >
                                                                {bundle.status === Status.ACTIVE ? (
                                                                    <Ban className="h-4 w-4" />
                                                                ) : (
                                                                    <Check className="h-4 w-4" />
                                                                )}
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                title="Delete policy bundle"
                                                                className="text-red-600 hover:text-red-700"
                                                                onClick={() => {
                                                                    setPendingDeleteBundle(bundle);
                                                                }}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                title="Manage policy users"
                                                                onClick={() => {
                                                                    runInBackground(
                                                                        Promise.resolve(
                                                                            breadcrumbNavigate({
                                                                                source: 'panel',
                                                                                target: {
                                                                                    url:
                                                                                        `/organizations/${props.organizationUuid}` +
                                                                                        `/permissions-policies/` +
                                                                                        `${bundle.permissions_bundle_uuid}/users`,
                                                                                    title: 'Policy Role Assignments',
                                                                                },
                                                                            })
                                                                        ),
                                                                        () => {}
                                                                    );
                                                                }}
                                                            >
                                                                <User className="h-4 w-4" />
                                                            </Button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                title="View policy bundle"
                                                                onClick={() => {
                                                                    setSelectedBundle(bundle);
                                                                    setIsViewModalOpen(true);
                                                                }}
                                                            >
                                                                <Eye className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                title="Undelete policy bundle"
                                                                onClick={() => {
                                                                    setActiveMutation.mutate({
                                                                        permissionsBundleUuid:
                                                                            bundle.permissions_bundle_uuid,
                                                                        isActive: true,
                                                                    });
                                                                }}
                                                                disabled={
                                                                    setActiveMutation.isPending
                                                                }
                                                            >
                                                                Undelete
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                                </div>
                                            );
                                        }
                                    )}
                                    <div
                                        ref={list.loadMoreSentinelRef}
                                        className={props.compactBottomSpacing ? 'h-0' : 'h-8'}
                                    />
                                </div>
                                {list.isFetching ? (
                                    <InfiniteScrollListLoadingState label="Loading policy bundles..." />
                                ) : null}
                            </>
                        )}
                    </CardContent>
                ) : null}
            </Card>

            <PolicyPermissionBundleModal
                open={isCreateModalOpen}
                onOpenChange={setIsCreateModalOpen}
                mode="create"
                organizationId={null}
                organizationUuid={props.organizationUuid}
                organizationName={props.organizationName}
                bundle={null}
            />

            <PolicyPermissionBundleModal
                open={isViewModalOpen}
                onOpenChange={(open) => {
                    setIsViewModalOpen(open);
                    if (!open) {
                        setSelectedBundle(null);
                    }
                }}
                mode="view"
                organizationId={null}
                organizationUuid={props.organizationUuid}
                organizationName={props.organizationName}
                bundle={selectedBundle}
            />

            <PolicyPermissionBundleModal
                open={isEditModalOpen}
                onOpenChange={(open) => {
                    setIsEditModalOpen(open);
                    if (!open) {
                        setSelectedBundle(null);
                    }
                }}
                mode="edit"
                organizationId={null}
                organizationUuid={props.organizationUuid}
                organizationName={props.organizationName}
                bundle={selectedBundle}
            />

            <ConfirmationModal
                open={pendingDeleteBundle !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setPendingDeleteBundle(null);
                    }
                }}
                message="Delete this policy bundle?"
                confirmLabel="Delete"
                onConfirm={async () => {
                    const bundleToDelete = pendingDeleteBundle;
                    if (bundleToDelete === null) {
                        return;
                    }
                    await deleteMutation.mutateAsync(bundleToDelete.permissions_bundle_uuid);
                }}
                onCancel={() => {
                    setPendingDeleteBundle(null);
                }}
                isLoading={deleteMutation.isPending}
            />
        </div>
    );
}
